import { useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { ResultUseMatches } from '../../common/types/use-matches/index.tsx';
import { hasChildren, mergeSections } from '../../common/utils.tsx';
import { getActiveCommandList } from '../../common/utils/get-active-command-list/index.tsx';
import {
	getFallbackCommandResult,
	getFallbackCommandResultOld,
} from '../../common/utils/get-fallback-command-result/index.tsx';
import { useCommandPalette } from '../command-palette/index.tsx';
import { useCommandPaletteSearchDebounced } from '../use-command-palette-search-debounced/index.tsx';
import type { UseMatchesArgs } from './types.tsx';
import { getFallback } from './utils/get-fallback/index.tsx';
import { useSortAndFilter } from './utils/sort-and-filter/index.tsx';

export const useMatches = ({ defaultFallback, rootFallback }: UseMatchesArgs): ResultUseMatches => {
	const [
		{
			commands,
			expandedCommand,
			childResult,
			loadingList,
			sections,
			fallbackOverride,
			activeIndex,
		},
	] = useCommandPalette();
	const [search] = useCommandPaletteSearchDebounced();
	const hasLoadChildren = hasChildren(expandedCommand);

	const selectedCommands = useMemo(
		() => (hasLoadChildren ? childResult?.commands || [] : commands),
		[hasLoadChildren, childResult?.commands, commands],
	);

	const selectedSections = useMemo(
		() => (hasLoadChildren ? childResult?.sections : mergeSections(sections)),
		[hasLoadChildren, childResult?.sections, sections],
	);

	const filterCommandsOverride = useMemo(
		() =>
			getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)
				?.filterAndSortOverrideFn,
		[expandedCommand],
	);

	const flattenedResults = useSortAndFilter(
		search,
		selectedCommands,
		selectedSections,
		filterCommandsOverride,
	);

	const fallback = useMemo(
		() =>
			getFallback({
				rootFallback,
				defaultFallback,
				expandedCommand,
				noResults: Boolean(flattenedResults.results.length === 0 && !loadingList),
				fallbackOverride,
			}),
		[
			defaultFallback,
			expandedCommand,
			fallbackOverride,
			flattenedResults.results.length,
			loadingList,
			rootFallback,
		],
	);

	return useMemo(() => {
		if (
			getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)
				?.shouldShowNoCommandsWhileLoading &&
			loadingList
		) {
			return {
				results: [],
				total: 0,
			};
		}
		const fallbackCommandResult = fg('jjj_ask_rovo_compal_v1')
			? getFallbackCommandResult(fallback, activeIndex)
			: getFallbackCommandResultOld(fallback);

		if (fallbackCommandResult) {
			return fallbackCommandResult;
		}

		return {
			...flattenedResults,
			fallback,
		};
	}, [expandedCommand, fallback, flattenedResults, loadingList, activeIndex]);
};
